import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Post, Comment, Profile, PostMedia, PostLike, PostTag, ReactionType } from '../types/database';
import { formatDistanceToNow } from 'date-fns';
import { MoreVertical, Pencil, Trash2, Link, Users } from 'lucide-react';
import CreatePost from '../components/CreatePost';
import EditPost from '../components/EditPost';
import Comments from '../components/Comments';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import MediaGallery from '../components/MediaGallery';
import UserVisibilityControls from '../components/UserVisibilityControls';
import { unfurl } from 'unfurl.js';
import PostInteractions from '../components/PostInteractions';
import { useUrlPreviews } from '../hooks/useUrlPreviews';

interface PostWithCounts extends Post {
  likes_count?: number;
  comments_count?: number;
  user_has_liked?: boolean;
  profiles?: Profile;
  post_media?: PostMedia[];
  post_tags?: (PostTag & { tagged_user?: Profile })[];
}

const POSTS_PER_PAGE = 5;

interface FeedProps {
  searchQuery?: string;
}

export default function Feed({ searchQuery = '' }: FeedProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<PostWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [menuOpenPostId, setMenuOpenPostId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCommentsForPost, setShowCommentsForPost] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, Comment[]>>({});
  const [loadingComments, setLoadingComments] = useState<Record<string, boolean>>({});
  const lastPostRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const postRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollToPostIdRef = useRef<string | null>(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const [fullscreenGallery, setFullscreenGallery] = useState<{ media: PostMedia[]; currentIndex: number } | null>(null);
  const { renderContent, extractUrls, fetchUrlMetadata } = useUrlPreviews();

  useEffect(() => {
    if (user) {
      if (searchQuery.trim()) {
        searchPosts(true);
      } else {
        fetchPosts(true);
      }
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [user, searchQuery]);

  useEffect(() => {
    if (!loading && posts.length > 0 && scrollToPostIdRef.current) {
      const scrollToPostId = scrollToPostIdRef.current;
      const openCommentId = sessionStorage.getItem('openCommentId');
      
      const postIndex = posts.findIndex(post => post.id === scrollToPostId);
      
      if (postIndex >= 0) {
        const postRef = postRefs.current[scrollToPostId];
        if (postRef) {
          setTimeout(() => {
            postRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            if (openCommentId) {
              setShowCommentsForPost(scrollToPostId);
              fetchComments(scrollToPostId).then(() => {
                setHighlightedCommentId(openCommentId);
              });
            }
            
            postRef.classList.add('bg-blue-50');
            setTimeout(() => {
              postRef.classList.remove('bg-blue-50');
            }, 2000);
            
            sessionStorage.removeItem('scrollToPostId');
            sessionStorage.removeItem('openCommentId');
            scrollToPostIdRef.current = null;
          }, 100);
        }
      } else if (hasMore) {
        fetchPosts();
      } else {
        sessionStorage.removeItem('scrollToPostId');
        sessionStorage.removeItem('openCommentId');
        scrollToPostIdRef.current = null;
      }
    }
  }, [posts, loading]);

  const lastPostCallback = useCallback((node: HTMLDivElement | null) => {
    if (loading || loadingMore) return;

    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        if (searchQuery.trim()) {
          searchPosts();
        } else {
          fetchPosts();
        }
      }
    });

    if (node) {
      observer.current.observe(node);
    }
  }, [loading, loadingMore, hasMore, searchQuery]);

  const handleReaction = async (post: Post, reactionType: ReactionType) => {
    if (!user) return;

    try {
      setError(null);

      if (post.user_reaction_type === reactionType) {
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', post.id)
          .eq('user_id', user.id);

        if (error) throw error;

        setPosts(posts.map(p =>
          p.id === post.id
            ? {
                ...p,
                likes_count: Math.max((p.likes_count || 0) - 1, 0),
                user_has_liked: false,
                user_reaction_type: null
              }
            : p
        ));
      } else if (post.user_reaction_type) {
        const { error } = await supabase
          .from('post_likes')
          .update({ reaction_type: reactionType })
          .eq('post_id', post.id)
          .eq('user_id', user.id);

        if (error) throw error;

        setPosts(posts.map(p =>
          p.id === post.id
            ? {
                ...p,
                user_reaction_type: reactionType
              }
            : p
        ));
      } else {
        const { error } = await supabase
          .from('post_likes')
          .insert({
            post_id: post.id,
            user_id: user.id,
            reaction_type: reactionType
          });

        if (error) throw error;

        setPosts(posts.map(p =>
          p.id === post.id
            ? {
                ...p,
                likes_count: (p.likes_count || 0) + 1,
                user_has_liked: true,
                user_reaction_type: reactionType
              }
            : p
        ));
      }
    } catch (error: any) {
      console.error('Error toggling reaction:', error);
      setError('Failed to update reaction. Please try again.');
    }
  };

  const fetchComments = async (postId: string) => {
    if (loadingComments[postId]) return;

    try {
      setLoadingComments(prev => ({ ...prev, [postId]: true }));

      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select(`
          *,
          profiles (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (commentsError) throw commentsError;

      setComments(prev => ({ ...prev, [postId]: commentsData || [] }));
    } catch (error: any) {
      console.error('Error fetching comments:', error);
      setError('Failed to load comments. Please try again.');
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const toggleComments = async (postId: string) => {
    if (showCommentsForPost === postId) {
      setShowCommentsForPost(null);
      setHighlightedCommentId(null);
    } else {
      setShowCommentsForPost(postId);
      if (!comments[postId]) {
        await fetchComments(postId);
      }
    }
  };

  const handleCommentAdded = async (postId: string, newComment: Comment & { profiles?: Profile }) => {
    setComments(prev => ({
      ...prev,
      [postId]: [...(prev[postId] || []), newComment]
    }));

    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, comments_count: (post.comments_count || 0) + 1 }
        : post
    ));
  };

  const getAvatarUrl = (profile?: Profile | null) => {
    if (!profile) return `https://api.dicebear.com/7.x/initials/svg?seed=anonymous`;

    if (profile.avatar_url) {
      if (profile.avatar_url.startsWith('http')) {
        return profile.avatar_url;
      }
      const { data } = supabase.storage.from('avatars').getPublicUrl(profile.avatar_url);
      return data.publicUrl;
    }

    return `https://api.dicebear.com/7.x/initials/svg?seed=${profile.full_name}`;
  };

  const handleDeletePost = async (postId: string) => {
    if (!user) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this post?');
    if (!confirmDelete) return;

    try {
      setError(null);
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId)
        .eq('user_id', user.id);

      if (error) throw error;

      setPosts(posts.filter(post => post.id !== postId));
      setMenuOpenPostId(null);
    } catch (error: any) {
      console.error('Error deleting post:', error);
      setError(error.message || 'Failed to delete post. Please try again.');
    }
  };

  const fetchPosts = async (isInitial = false) => {
    if (!user) return;

    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const { data: friendships, error: friendError } = await supabase
        .from('friends')
        .select('sender_id, receiver_id')
        .eq('status', 'accepted')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`);

      if (friendError) throw friendError;

      const friendIds = friendships?.reduce((acc: string[], friendship) => {
        if (friendship.sender_id === user.id) {
          acc.push(friendship.receiver_id);
        } else {
          acc.push(friendship.sender_id);
        }
        return acc;
      }, []) || [];

      const allowedUserIds = [user.id, ...friendIds];

      const { data: preferences, error: prefError } = await supabase
        .from('user_preferences')
        .select('target_user_id')
        .eq('user_id', user.id)
        .eq('is_following', false);

      if (prefError) throw prefError;

      const unfollowedIds = new Set(preferences?.map(p => p.target_user_id) || []);
      const followedUserIds = allowedUserIds.filter(id => !unfollowedIds.has(id));

      console.log('Fetching posts for user:', user.id);
      console.log('Following user IDs:', followedUserIds);
      
      // Get posts from followed users
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select(`
          *,
          profiles!posts_user_id_fkey (
            id,
            full_name,
            avatar_url
          ),
          post_likes (count),
          comments (count),
          post_media (
            id,
            url,
            type,
            position
          ),
          post_tags (
            id,
            tagged_user_id,
            tagged_by_user_id,
            tagged_user:profiles!post_tags_tagged_user_id_fkey (
              id,
              full_name,
              avatar_url
            )
          )
        `)
        .in('user_id', followedUserIds)
        .order('created_at', { ascending: false })
        .range(isInitial ? 0 : posts.length, isInitial ? POSTS_PER_PAGE - 1 : posts.length + POSTS_PER_PAGE - 1);

      if (postsError) {
        console.error('Posts fetch error:', postsError);
        throw postsError;
      }
      
      console.log('Posts fetched:', postsData?.length || 0);

      // Get posts where user is tagged (separate query to avoid logic tree issues)
      const { data: taggedPostsData, error: taggedPostsError } = await supabase
        .from('posts')
        .select(`
          *,
          profiles!posts_user_id_fkey (
            id,
            full_name,
            avatar_url
          ),
          post_likes (count),
          comments (count),
          post_media (
            id,
            url,
            type,
            position
          ),
          post_tags!inner (
            id,
            tagged_user_id,
            tagged_by_user_id,
            tagged_user:profiles!post_tags_tagged_user_id_fkey (
              id,
              full_name,
              avatar_url
            )
          )
        `)
        .eq('post_tags.tagged_user_id', user.id)
        .order('created_at', { ascending: false })
        .range(isInitial ? 0 : posts.length, isInitial ? POSTS_PER_PAGE - 1 : posts.length + POSTS_PER_PAGE - 1);

      if (taggedPostsError) {
        console.error('Tagged posts fetch error:', taggedPostsError);
        // Don't throw error, just continue with regular posts
      }

      console.log('Tagged posts fetched:', taggedPostsData?.length || 0);

      const { data: userReactions } = await supabase
        .from('post_likes')
        .select('post_id, reaction_type')
        .eq('user_id', user.id);

      const userReactionsMap = new Map(userReactions?.map(r => [r.post_id, r.reaction_type]) || []);

      // Combine and deduplicate posts
      const allPosts = [...(postsData || []), ...(taggedPostsData || [])];
      const uniquePosts = allPosts.reduce((acc, post) => {
        if (!acc.find(p => p.id === post.id)) {
          acc.push(post);
        }
        return acc;
      }, [] as typeof allPosts);

      // Sort by created_at descending
      uniquePosts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      const enrichedPosts = uniquePosts.map(post => ({
        ...post,
        likes_count: post.post_likes?.[0]?.count || 0,
        comments_count: post.comments?.[0]?.count || 0,
        user_has_liked: userReactionsMap.has(post.id),
        user_reaction_type: userReactionsMap.get(post.id) || null,
        post_media: post.post_media?.sort((a, b) => a.position - b.position) || [],
      }));

      setPosts(prev => isInitial ? enrichedPosts : [...prev, ...enrichedPosts]);
      setHasMore(uniquePosts.length >= POSTS_PER_PAGE);
      
      if (isInitial) {
        const scrollToPostId = sessionStorage.getItem('scrollToPostId');
        if (scrollToPostId) {
          scrollToPostIdRef.current = scrollToPostId;
        }
      }

      // Process URLs in post descriptions
      enrichedPosts.forEach(post => {
        if (post.description) {
          const urls = extractUrls(post.description);
          urls.forEach(url => {
            fetchUrlMetadata(url);
          });
        }
      });
    } catch (err: any) {
      console.error('Error fetching posts:', err);
      setError(err.message || 'Failed to load posts. Please try refreshing the page.');
    } finally {
      if (isInitial) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const searchPosts = async (isInitial = false) => {
    if (!user || !searchQuery.trim()) return;

    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const { data: friendships, error: friendError } = await supabase
        .from('friends')
        .select('sender_id, receiver_id')
        .eq('status', 'accepted')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`);

      if (friendError) throw friendError;

      const friendIds = friendships?.reduce((acc: string[], friendship) => {
        if (friendship.sender_id === user.id) {
          acc.push(friendship.receiver_id);
        } else {
          acc.push(friendship.sender_id);
        }
        return acc;
      }, []) || [];

      const allowedUserIds = [user.id, ...friendIds];

      const { data: preferences, error: prefError } = await supabase
        .from('user_preferences')
        .select('target_user_id')
        .eq('user_id', user.id)
        .eq('is_following', false);

      if (prefError) throw prefError;

      const unfollowedIds = new Set(preferences?.map(p => p.target_user_id) || []);
      const followedUserIds = allowedUserIds.filter(id => !unfollowedIds.has(id));

      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select(`
          *,
          profiles!posts_user_id_fkey (
            id,
            full_name,
            avatar_url
          ),
          post_likes (count),
          comments (count),
          post_media (
            id,
            url,
            type,
            position
          ),
          post_tags (
            id,
            tagged_user_id,
            tagged_by_user_id,
            tagged_user:profiles!post_tags_tagged_user_id_fkey (
              id,
              full_name,
              avatar_url
            )
          )
        `)
        .in('user_id', followedUserIds)
        .textSearch('search_vector', searchQuery, {
          type: 'websearch',
          config: 'english'
        })
        .order('created_at', { ascending: false })
        .range(isInitial ? 0 : posts.length, isInitial ? POSTS_PER_PAGE - 1 : posts.length + POSTS_PER_PAGE - 1);

      if (postsError) throw postsError;

      const { data: taggedPostsData, error: taggedPostsError } = await supabase
        .from('posts')
        .select(`
          *,
          profiles!posts_user_id_fkey (
            id,
            full_name,
            avatar_url
          ),
          post_likes (count),
          comments (count),
          post_media (
            id,
            url,
            type,
            position
          ),
          post_tags!inner (
            id,
            tagged_user_id,
            tagged_by_user_id,
            tagged_user:profiles!post_tags_tagged_user_id_fkey (
              id,
              full_name,
              avatar_url
            )
          )
        `)
        .eq('post_tags.tagged_user_id', user.id)
        .textSearch('search_vector', searchQuery, {
          type: 'websearch',
          config: 'english'
        })
        .order('created_at', { ascending: false })
        .range(isInitial ? 0 : posts.length, isInitial ? POSTS_PER_PAGE - 1 : posts.length + POSTS_PER_PAGE - 1);

      if (taggedPostsError) {
        console.error('Tagged posts search error:', taggedPostsError);
      }

      const { data: userReactions } = await supabase
        .from('post_likes')
        .select('post_id, reaction_type')
        .eq('user_id', user.id);

      const userReactionsMap = new Map(userReactions?.map(r => [r.post_id, r.reaction_type]) || []);

      const allPosts = [...(postsData || []), ...(taggedPostsData || [])];
      const uniquePosts = allPosts.reduce((acc, post) => {
        if (!acc.find(p => p.id === post.id)) {
          acc.push(post);
        }
        return acc;
      }, [] as typeof allPosts);

      uniquePosts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      const enrichedPosts = uniquePosts.map(post => ({
        ...post,
        likes_count: post.post_likes?.[0]?.count || 0,
        comments_count: post.comments?.[0]?.count || 0,
        user_has_liked: userReactionsMap.has(post.id),
        user_reaction_type: userReactionsMap.get(post.id) || null,
        post_media: post.post_media?.sort((a, b) => a.position - b.position) || [],
      }));

      setPosts(prev => isInitial ? enrichedPosts : [...prev, ...enrichedPosts]);
      setHasMore(uniquePosts.length >= POSTS_PER_PAGE);

      enrichedPosts.forEach(post => {
        if (post.description) {
          const urls = extractUrls(post.description);
          urls.forEach(url => {
            fetchUrlMetadata(url);
          });
        }
      });
    } catch (err: any) {
      console.error('Error searching posts:', err);
      setError(err.message || 'Failed to search posts. Please try again.');
    } finally {
      if (isInitial) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const renderTaggedFriends = (post: PostWithCounts) => {
    if (!post.post_tags || post.post_tags.length === 0) return null;

    const taggedUsers = post.post_tags
      .map(tag => tag.tagged_user)
      .filter(Boolean) as Profile[];

    if (taggedUsers.length === 0) return null;

    const displayNames = taggedUsers.slice(0, 3);
    const remainingCount = taggedUsers.length - 3;

    return (
      <span className="text-sm text-gray-500 inline">
        {' is with '}
        {displayNames.map((user, index) => (
          <React.Fragment key={user.id}>
            <button
              onClick={() => navigate(`/user/${user.id}`)}
              className="text-blue-500 hover:text-blue-700 hover:underline"
            >
              {user.full_name}
            </button>
            {index < displayNames.length - 1 && (
              <span>{index === displayNames.length - 2 ? ' and ' : ', '}</span>
            )}
          </React.Fragment>
        ))}
        {remainingCount > 0 && (
          <span> and {remainingCount} other{remainingCount > 1 ? 's' : ''}</span>
        )}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const handlePostCreated = () => {
    if (searchQuery.trim()) {
      searchPosts(true);
    } else {
      fetchPosts(true);
    }
  };

  return (
    <div className="space-y-6">
      <CreatePost onPostCreated={handlePostCreated} />
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {posts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
          <p className="text-lg">
            {searchQuery.trim()
              ? `No posts found matching "${searchQuery}"`
              : 'No posts yet. Be the first to share something!'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post, index) => {
            const isMenuOpen = menuOpenPostId === post.id;
            const isOwnPost = user && post.user_id === user.id;
            const isShowingComments = showCommentsForPost === post.id;
            const isLastPost = index === posts.length - 1;
            const isEditing = editingPostId === post.id;

            if (isEditing) {
              return (
                <EditPost
                  key={`edit-${post.id}`}
                  postId={post.id}
                  initialDescription={post.description || ''}
                  initialMedia={post.post_media || []}
                  initialTags={post.post_tags?.map(tag => tag.tagged_user).filter(Boolean) as Profile[] || []}
                  onSave={() => {
                    setEditingPostId(null);
                    fetchPosts(true);
                  }}
                  onCancel={() => setEditingPostId(null)}
                />
              );
            }

            return (
              <div
                key={`post-${post.id}-${index}`}
                ref={(el) => {
                  postRefs.current[post.id] = el;
                  if (isLastPost) lastPostCallback(el);
                }}
                className="bg-white rounded-lg shadow transition-colors duration-300"
                id={`post-${post.id}`}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <img
                        src={getAvatarUrl(post.profiles)}
                        alt={post.profiles?.full_name || 'Anonymous'}
                        className={cn(
                          "h-10 w-10 rounded-full object-cover bg-gray-100",
                          post.profiles?.id && "cursor-pointer hover:opacity-80"
                        )}
                        onClick={() => post.profiles?.id && navigate(`/user/${post.profiles.id}`)}
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${post.profiles?.full_name || 'anonymous'}`;
                        }}
                      />
                      <div>
                        <p 
                          className={cn(
                            "font-medium text-gray-900",
                            post.profiles?.id && "cursor-pointer hover:text-blue-600"
                          )}
                        >
                          <span
                            onClick={() => post.profiles?.id && navigate(`/user/${post.profiles.id}`)}
                            className="cursor-pointer hover:text-blue-600"
                          >
                            {post.profiles?.full_name || 'Anonymous User'}
                          </span>
                          <span className="inline">
                            {renderTaggedFriends(post)}
                          </span>
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {!isOwnPost && user && (
                        <UserVisibilityControls
                          userId={user.id}
                          targetUserId={post.user_id}
                          onVisibilityChange={() => fetchPosts(true)}
                        />
                      )}
                      
                      {isOwnPost && (
                        <div className="relative">
                          <button
                            onClick={() => setMenuOpenPostId(isMenuOpen ? null : post.id)}
                            className="p-2 rounded-full hover:bg-gray-100 focus:outline-none"
                          >
                            <MoreVertical className="h-5 w-5 text-gray-500" />
                          </button>

                          {isMenuOpen && (
                            <div className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                              <div className="py-1" role="menu">
                                <button
                                  onClick={() => {
                                    setEditingPostId(post.id);
                                    setMenuOpenPostId(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Post
                                </button>
                                <button
                                  onClick={() => handleDeletePost(post.id)}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Post
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {post.description && renderContent(post.description)}
                  
                  {post.post_media && post.post_media.length > 0 && (
                    <div className="mt-4">
                      <MediaGallery
                        media={post.post_media}
                        onMediaClick={(index) => {
                          setFullscreenGallery({
                            media: post.post_media || [],
                            currentIndex: index
                          });
                        }}
                      />
                    </div>
                  )}

                  <div className="mt-4">
                    <PostInteractions
                      postId={post.id}
                      likesCount={post.likes_count || 0}
                      commentsCount={post.comments_count || 0}
                      userReactionType={post.user_reaction_type}
                      onReaction={(reactionType) => handleReaction(post, reactionType)}
                      onComment={() => toggleComments(post.id)}
                    />
                  </div>

                  {isShowingComments && (
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      {loadingComments[post.id] ? (
                        <div className="flex justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                        </div>
                      ) : (
                        <Comments
                          postId={post.id}
                          comments={comments[post.id] || []}
                          onCommentAdded={(newComment) => handleCommentAdded(post.id, newComment)}
                          highlightCommentId={highlightedCommentId}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {loadingMore && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          )}
        </div>
      )}

      {fullscreenGallery && (
        <MediaGallery
          media={fullscreenGallery.media}
          onMediaClick={(index) => {
            setFullscreenGallery({
              ...fullscreenGallery,
              currentIndex: index
            });
          }}
          fullscreen
          currentIndex={fullscreenGallery.currentIndex}
          onClose={() => setFullscreenGallery(null)}
          onPrevious={() => {
            if (fullscreenGallery.currentIndex > 0) {
              setFullscreenGallery({
                ...fullscreenGallery,
                currentIndex: fullscreenGallery.currentIndex - 1
              });
            }
          }}
          onNext={() => {
            if (fullscreenGallery.currentIndex < fullscreenGallery.media.length - 1) {
              setFullscreenGallery({
                ...fullscreenGallery,
                currentIndex: fullscreenGallery.currentIndex + 1
              });
            }
          }}
        />
      )}
    </div>
  );
}